import { useState, useEffect, useRef, useCallback } from 'react';
import { eventBus, EventNames } from '../utils/eventBus';
import type { DocumentFilters, SharedFilters } from '../services/api';
import type { Document as ApiDocument } from '../services/api';
import logger from '../utils/simpleLogger';

interface UseDriveFiltersProps {
  privateDocuments: ApiDocument[];
  sharedDocuments: ApiDocument[];
  // projectId removed - project system eliminated
  onDocumentSelect?: (documentIds: string[]) => void;
  setInternalSelectedDocuments: (updater: (prev: string[]) => string[]) => void;
}

export const useDriveFilters = ({
  privateDocuments,
  sharedDocuments,
  onDocumentSelect,
  setInternalSelectedDocuments,
}: UseDriveFiltersProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInContent, setSearchInContent] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'document' | 'saved-message' | 'snippet'>(
    'all'
  );
  const [originFilter, setOriginFilter] = useState<'all' | 'shared' | 'private'>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [advancedFilters, setAdvancedFilters] = useState<DocumentFilters>({});
  const [availableFilters, setAvailableFilters] = useState<SharedFilters>({
    companies: [],
    types: [],
    periods: [],
  });

  // Filtros CORE
  const [filterItemType, setFilterItemType] = useState<
    'all' | 'document' | 'saved-message' | 'snippet' | 'studio-result' | 'shared' | 'private'
  >('all');
  const [filterOrigin, setFilterOrigin] = useState<'all' | 'private' | 'shared'>('all');
  const [filterPeriod, setFilterPeriod] = useState<string>('all');
  const [filterDocType, setFilterDocType] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<string | string[]>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Refs para rastrear los últimos valores establecidos por menciones
  const lastMentionCompanyRef = useRef<string | string[] | null>(null);
  const lastMentionDocTypeRef = useRef<string | null>(null);
  const lastMentionPeriodRef = useRef<string | null>(null);

  // Ref para rastrear si la selección actual fue hecha por menciones
  const selectionFromMentionsRef = useRef<boolean>(false);

  // Ref para rastrear si es el primer render (evitar emitir evento en mount)
  const isFirstRenderRef = useRef<boolean>(true);

  // Refs para mantener los valores actuales de los filtros
  const filterCompanyRef = useRef(filterCompany);
  const filterDocTypeRef = useRef(filterDocType);
  const filterPeriodRef = useRef(filterPeriod);

  // Update refs when filters change
  useEffect(() => {
    filterCompanyRef.current = filterCompany;
  }, [filterCompany]);

  useEffect(() => {
    filterDocTypeRef.current = filterDocType;
  }, [filterDocType]);

  useEffect(() => {
    filterPeriodRef.current = filterPeriod;
  }, [filterPeriod]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to calculate which documents match the filters
  const getMatchingDocumentIds = useCallback(
    (filters: {
      company?: string | string[];
      document_type?: string;
      document_period?: string;
    }): string[] => {
      const matchingIds: string[] = [];

      // Combine private and shared documents
      const allDocuments = [
        ...privateDocuments.filter(
          (doc) => doc.scope !== 'shared' // projectId removed - filter by scope only
        ),
        ...sharedDocuments,
      ];

      allDocuments.forEach((doc) => {
        let matches = true;

        // Filter by company
        if (filters.company) {
          const companies = Array.isArray(filters.company) ? filters.company : [filters.company];
          const docCompany = doc.company_name?.toLowerCase() || '';
          matches = matches && companies.some((company) => docCompany === company.toLowerCase());
        }

        // Filter by document type
        if (matches && filters.document_type) {
          const docType = doc.document_type?.toLowerCase() || '';
          matches = matches && docType === filters.document_type.toLowerCase();
        }

        // Filter by period
        if (matches && filters.document_period) {
          matches = matches && doc.document_period === filters.document_period;
        }

        if (matches) {
          matchingIds.push(doc.id);
        }
      });

      return matchingIds;
    },
    [privateDocuments, sharedDocuments] // projectId removed
  );

  // DISABLED: Listen for filters from mentions and sync with Drive
  // Chat → Drive synchronization removed to avoid sync issues
  // Drive filters are now independent from chat mentions
  // Only Drive → Chat synchronization remains active
  // useEffect(() => {
  //   const handleFiltersFromMentions = (filters: {
  //     company?: string | string[];
  //     document_type?: string;
  //     document_period?: string;
  //   }) => {
  //     // ... handler code removed ...
  //   };
  //   eventBus.on(EventNames.DRIVE_FILTERS_FROM_MENTIONS, handleFiltersFromMentions);
  //   return () => {
  //     eventBus.off(EventNames.DRIVE_FILTERS_FROM_MENTIONS, handleFiltersFromMentions);
  //   };
  // }, []);

  // Load available filters
  const loadAvailableFilters = useCallback(async () => {
    try {
      const { apiService } = await import('../services/api');
      const response = await apiService.getSharedFilters();
      if (response.success) {
        setAvailableFilters(response.filters || { companies: [], types: [], periods: [] });
      }
    } catch (error) {
      console.error('Error loading filters:', error);
    }
  }, []);

  useEffect(() => {
    loadAvailableFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = useCallback(() => {
    setFilterItemType('all');
    setFilterPeriod('all');
    setFilterDocType('all');
    setFilterCompany('all');
    setSearchTerm('');
  }, []);

  // Reset filters when a new conversation starts (prevents stale state leaking)
  useEffect(() => {
    const handler = () => {
      handleReset();
    };
    eventBus.on(EventNames.NEW_CONVERSATION, handler);
    return () => {
      eventBus.off(EventNames.NEW_CONVERSATION, handler);
    };
  }, [handleReset]);

  // Emitir evento cuando cambien los filtros locales del Drive
  // (solo si el cambio NO viene de menciones)
  useEffect(() => {
    // Skip en el primer render para evitar emitir evento al montar
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    // Skip si este cambio viene de menciones para evitar loops
    if (selectionFromMentionsRef.current) {
      selectionFromMentionsRef.current = false;
      return;
    }

    // Construir objeto de filtros para enviar a ChatInputArea
    const filters: {
      company?: string | string[];
      document_type?: string;
      document_period?: string;
    } = {};

    if (filterCompany !== 'all') {
      filters.company = filterCompany;
    }
    if (filterDocType !== 'all') {
      filters.document_type = filterDocType;
    }
    if (filterPeriod !== 'all') {
      filters.document_period = filterPeriod;
    }

    // Emitir evento para sincronizar con ChatInputArea
    // Solo emitir si hay al menos un filtro activo o si acabamos de limpiar todos
    const hasFilters = Object.keys(filters).length > 0;
    const allFiltersCleared =
      filterCompany === 'all' && filterDocType === 'all' && filterPeriod === 'all';

    if (hasFilters || allFiltersCleared) {
      eventBus.emit(EventNames.DRIVE_FILTERS_TO_MENTIONS, filters);
      logger.info('📤 Drive → Chat: Filtros actualizados', filters);
    }
  }, [filterCompany, filterDocType, filterPeriod]);

  return {
    // Search
    searchTerm,
    setSearchTerm,
    searchInContent,
    setSearchInContent,
    debouncedSearchTerm,
    // Filters
    filterType,
    setFilterType,
    originFilter,
    setOriginFilter,
    selectedTags,
    setSelectedTags,
    advancedFilters,
    setAdvancedFilters,
    availableFilters,
    setAvailableFilters,
    // Core filters
    filterItemType,
    setFilterItemType,
    filterOrigin,
    setFilterOrigin,
    filterPeriod,
    setFilterPeriod,
    filterDocType,
    setFilterDocType,
    filterCompany,
    setFilterCompany,
    filterStatus,
    setFilterStatus,
    // Functions
    getMatchingDocumentIds,
    loadAvailableFilters,
    handleReset,
  };
};
